import React from 'react';
import clsx from 'clsx';
import makeStyles from '@material-ui/core/styles/makeStyles';
import DottedDivider from './DottedDivider';
import ComingSoonContent from './ComingSoonContent';
import Typography from '@material-ui/core/Typography';
import ContentTitle from './ContentTitle';
import ServiceButton from './ServiceButton';
import MapIcon from '../../../../images/features/local_delivery/map-marker-line.png';
import ExternalIcon from '../../../../images/features/local_delivery/external_icon.png';
import ImageSlider from './ImageSlider';
import { BACKGROUND_NUMBER_COLOR } from '../constants';
import { BUSINESS_URL, PLAN } from '../../../front/routing';
import FollowButton from './FollowButton';
import StyledTypography from './StyledTypography';
import Grid from '@material-ui/core/Grid';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: theme.breakpoints.values.lg - 100,
    margin: 'auto',
  },
  container: {
    margin: `${theme.spacing(4)}px auto`,
    backgroundColor: 'white',
    borderRadius: 8,
    position: 'relative',

    padding: theme.spacing(8, 2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(8, 4),
    },
  },
  background: {
    position: 'absolute',
    top: -24,
    left: -6,
    color: BACKGROUND_NUMBER_COLOR,
    fontSize: theme.typography.pxToRem(80),
    fontWeight: 'bold',
    fontFamily: 'Montserrat, sans-serif',
    letterSpacing: 4,
  },
  head: {
    position: 'relative',
  },
  businessName: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 'bold',
    margin: theme.spacing(4, 0, 2),
    [theme.breakpoints.up('sm')]: {
      margin: theme.spacing(2, 0),
      width: 'calc(100% - 200px)',
    },
  },
  address: {
    color: 'grey',
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: theme.spacing(4),
    '&::before': {
      content: '""',
      backgroundImage: `url(${MapIcon})`,
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      display: 'inline-block',
      width: 14,
      height: 20,
      verticalAlign: 'middle',
      marginRight: theme.spacing(1),
    },
  },
  button: {
    position: 'absolute',
    top: -65,
    right: 0,
    padding: theme.spacing(0.75, 3),
    fontSize: theme.typography.pxToRem(11),
    [theme.breakpoints.up('sm')]: {
      width: 200,
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: theme.typography.pxToRem(14),
    },
  },
  externalIcon: {
    width: 15,
  },
  contents: {
    display: 'flex',

    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  content: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '50%',
    },
  },
  adminContainer: {
    display: 'flex',
  },
  adminImage: {
    width: 100,
    height: 100,
    borderRadius: '50%',
    marginRight: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      width: 140,
      height: 140,
    },
  },
  position: {
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(18),
    marginLeft: theme.spacing(1),
  },
  adminName: {
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(18),
    margin: theme.spacing(0, 0, 1, 1),
  },
  description: {
    '& p': {
      fontSize: theme.typography.pxToRem(14),
      lineHeight: 1.8,
    },

    margin: theme.spacing(2, 0, 4),
    [theme.breakpoints.up('md')]: {
      margin: '30px 32px 40px 0',
    },
  },
  tasks: {
    fontSize: theme.typography.pxToRem(14),
    margin: theme.spacing(2, 0, 4),
  },
  terms: {
    margin: theme.spacing(2, 0, 4),
  },
  term: {
    fontSize: theme.typography.pxToRem(14),
  },
  slickContainer: {
    maxWidth: 480,
    margin: `0 auto ${theme.spacing(2)}px`,
  },
  buttons: {
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  largeButton: {
    padding: theme.spacing(2, 0),
    width: '100%',
    maxWidth: 420,
  },
  caution: {
    fontSize: theme.typography.pxToRem(12),
    color: 'grey',
    textAlign: 'center',
    marginTop: theme.spacing(2),
  },
}));

const OtetsutabiListItem = ({
  authenticated,
  isComingSoon,
  order,
  planId,
  businessName,
  address,
  adminImage,
  position,
  name,
  description,
  tasks,
  terms,
  termText,
  images,
  isLast,
  business,
}) => {
  const classes = useStyles();
  const businessId = business?.id;
  const businessSiteId =
    business?.businessSiteId ?? business?.business_site_id ?? business?.id;
  return (
    <>
      <div className={classes.container}>
        <div className={classes.background}>#{order}</div>
        {isComingSoon ? (
          <ComingSoonContent />
        ) : (
          <>
            <div className={classes.head}>
              <Typography variant="h3" className={classes.businessName}>
                {businessName}
              </Typography>
              <ServiceButton
                classes={{ root: classes.button }}
                endIcon={
                  <img
                    src={ExternalIcon}
                    alt=""
                    className={classes.externalIcon}
                  />
                }
                href="http://sstv.jp/localdelivery"
                target="_blank"
                rel="noreferrer"
              >
                オンエア予定を確認
              </ServiceButton>
            </div>
            <Typography classes={{ root: classes.address }}>
              {address}
            </Typography>
            <div className={classes.contents}>
              <div className={classes.content}>
                <div className={classes.adminContainer}>
                  <img
                    src={adminImage}
                    alt="おてつたび先の方"
                    className={classes.adminImage}
                  />
                  <Grid container direction="column" justify="center">
                    <Grid item>
                      <Typography classes={{ root: classes.position }}>
                        {position}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography classes={{ root: classes.adminName }}>
                        {name}
                      </Typography>
                    </Grid>
                    {businessId != null && (
                      <Grid item>
                        <FollowButton
                          businessId={businessId}
                          businessSiteId={businessSiteId}
                          name={businessName}
                          authenticated={authenticated}
                          following={business.isFollowing}
                        />
                      </Grid>
                    )}
                  </Grid>
                </div>
                <div className={classes.description}>{description}</div>
                <ContentTitle variant="h4">おてつたびの内容</ContentTitle>
                <Typography classes={{ root: classes.tasks }}>
                  {tasks}
                </Typography>
                <ContentTitle variant="h4">おてつたびの募集日程</ContentTitle>
                <div className={classes.terms}>
                  {terms.map((term, index) => (
                    <StyledTypography
                      key={term}
                      classes={{ root: classes.term }}
                    >
                      日程{index + 1}：{term}
                    </StyledTypography>
                  ))}
                  <StyledTypography classes={{ root: classes.term }}>
                    {termText}
                  </StyledTypography>
                </div>
              </div>
              <div className={clsx(classes.content, classes.slickContainer)}>
                <ImageSlider images={images} />
              </div>
            </div>
            <Grid
              container
              spacing={2}
              alignItems="stretch"
              direction="column"
              justify="center"
              className={classes.buttons}
            >
              {businessId != null && (
                <Grid item xs={12} md={6}>
                  <ServiceButton
                    classes={{ root: classes.largeButton }}
                    rightArrow
                    href={
                      business?.url ||
                      BUSINESS_URL.replace(':businessId', businessId)
                    }
                  >
                    おてつたび先の詳細を見る
                  </ServiceButton>
                </Grid>
              )}
              {planId && (
                <Grid item xs={12} md={6}>
                  <ServiceButton
                    classes={{ root: classes.largeButton }}
                    rightArrow
                    href={PLAN.replace(':id', planId)}
                  >
                    今すぐおてつたびに応募
                  </ServiceButton>
                </Grid>
              )}
            </Grid>
            <Typography className={classes.caution}>
              ※おてつたび先のフォロー、応募には会員登録が必要です。
            </Typography>
          </>
        )}
      </div>
      {!isLast && <DottedDivider />}
    </>
  );
};

export default OtetsutabiListItem;
